export interface ASRItem {
  flow: string
  message: string
  timestamp: number
  sliceType: number
  index: number
}

export interface CallInfo {
  calleeLocation?: string
  calleePhoneNumberText?: string
  callerPhoneNumberText?: string
  callTimestamp?: number
  calleeName?: string
  leadId?: number
  cid?: string
  recordId?: number
}

export type CallStatusString = 'idle' | 'calling' | 'talking'

export enum AIAssistantSopNodeType {
  branch = 'branch',
  task = 'task',
}
export interface AIAssistantSopNode {
  id: string
  name: string
  type: AIAssistantSopNodeType
  words: string
  keywords: string
  childrenTitle?: string
  label?: string
  children?: AIAssistantSopNode[]
}

export interface CustomerProfile {
  componentName: string
  data: {
    actNum: string
    adoption: string
    branchNum: string
    companyName: string
    custIndustry1Name: string
    isChina500:string
    isIpo:string
    isProv100: string
    leadId: string
    prodClass4NameCombo: string
    registerCap: string
    stateOwnedEntrpr: string
  }
}
export interface IntentionSummary {
  componentName: string
  data: {
    intentionSummary: {
      'customerName': string,
      'position': string,
      'needProduct': string,
      'budget': string,
      'competitors': string,
      'businessDemand': string,
      'projectPhases': string,
      'businessMatchmaking': string,
      'industry': string,
      'monthConsumption': string,
      'contactPosition': string
    }}
}

export interface AIAssistantSearchNode {
  id: string
  label: string
  words: string
  done?: boolean
}

export enum AIAssistantNodeType {
  assistant = 'assistant',
  aiSearch = 'aiSearch',
}

export interface AIAssistantNode {
  id: string
  type: AIAssistantNodeType
  node: AIAssistantSopNode | AIAssistantSearchNode
}

// ---- GetAICallSummary ----

export interface AICallSummaryData {
  /** 状态 */
  status: number
  /** 通话摘要，JSON 字符串，解析后为 AICallSummaryParsed */
  callSummary: string
  /** 意向率分析，JSON 字符串，解析后为 AIIntentionRateParsed */
  intentionRate: string
  /** 跟进记录，JSON 字符串，解析后为 AIFollowRecordParsed */
  followRecord: string
  /** 商机建议，JSON 字符串，解析后为 AIOpportunitySuggestionParsed */
  opportunitySuggestion: string
  autoFillOpportunity?: {
    uin: string
    isNew: 0|1 // 0- 非新客 1 - 新客
  }
  /** 错误信息 */
  errorMsg: string
  /** 记录 ID */
  recordId: number
  /** 本次小结涉及的通话记录 ID 列表（对应 CallRecordListItem.recordId） */
  callRecordIds?: number[]
}

// callSummary 解析结果
export interface AICallSummaryParsed {
  call_summary: {
    /** 客户核心需求，以一段话概述 */
    customer_demand: string
    /** 沟通要点列表 */
    communication_points: string[]
    /** 客户顾虑/担忧点描述 */
    customer_concerns: string
  }
}

// intentionRate 解析结果
export interface AIIntentionRateParsed {
  intention_analysis: {
    /** 综合分数 0-100，各一级维度分数取平均 */
    overall_score: number
    dimensions: {
      /** 需求清晰度 */
      demand_clarity: {
        /** 一级维度分数 0-100 */
        score: number
        sub_dimensions: {
          /** 业务场景匹配度 0-100 */
          business_scenario_match: number
          /** 功能需求具体性 0-100 */
          functional_demand_specificity: number
          /** 时间紧迫性 0-100 */
          time_urgency: number
          /** 预算清晰度 0-100 */
          budget_clarity: number
        }
      }
      /** 决策倾向 */
      decision_tendency: {
        /** 一级维度分数 0-100 */
        score: number
        sub_dimensions: {
          /** 价格关注度 0-100 */
          price_attention: number
          /** 竞品对比行为 0-100 */
          competitor_comparison: number
          /** 逼单反应 0-100 */
          closing_reaction: number
          /** 订单确认意愿 0-100 */
          order_confirmation_willingness: number
        }
      }
      /** 意向深度 */
      intention_depth: {
        /** 一级维度分数 0-100 */
        score: number
        sub_dimensions: {
          /** 需求匹配接受度 0-100 */
          demand_match_acceptance: number
          /** 交叉营销响应 0-100 */
          cross_selling_response: number
          /** 问题深度 0-100 */
          question_depth: number
        }
      }
      /** 意向稳定性 */
      intention_stability: {
        /** 一级维度分数 0-100 */
        score: number
        sub_dimensions: {
          /** 异议类型 0-100 */
          objection_type: number
          /** 异议解决效果 0-100 */
          objection_resolution_effect: number
          /** 决策角色确认 0-100 */
          decision_role_confirmation: number
        }
      }
      /** 客户成熟度 */
      customer_maturity: {
        /** 一级维度分数 0-100 */
        score: number
        sub_dimensions: {
          /** 云经验水平 0-100 */
          cloud_experience_level: number
          /** 角色权重 0-100 */
          role_weight: number
          /** 自用场景 0-100 */
          self_use_scenario: number
        }
      }
    }
  }
}

// followRecord / opportunitySuggestion 的 follow_up_record 解析结果
export interface AICustomerIntention {
  /** 一级意向 ID */
  primary_intention_id: number
  /** 一级意向名称，如"意向" */
  primary_intention_name: string
  /** 二级意向 ID */
  secondary_intention_id: number
  /** 二级意向名称，如"高意向/中意向/低意向/无意向/待判断" */
  secondary_intention_name: string
}

export interface AIFollowUpRecord {
  /** 客户最新意向，级联：一级（意向/无意向/待判断）+ 二级（高/中/低/无/待判断） */
  customer_intention: AICustomerIntention
  /** 触达状态：已触达-有效沟通 | 已触达-无效沟通 | 未触达 */
  reach_status: {
    status_id: number,
    status_name: string
  }
  /** 本次跟进内容 */
  follow_content: string
  /** 竞对厂商 */
  competitor_vendor: string | null
  /** 竞对到期产品 */
  competitor_expiring_product: string[] | null
  /** 竞对产品到期时间 */
  competitor_product_expiry_time: string | null
  /** 下次跟进时间，ISO 8601 格式 */
  next_follow_time: string
  /** 待办提醒列表 */
  todo_reminders: Array<{
    /** 待办描述 */
    content: string
    /** 建议截止时间，ISO 8601 格式 */
    suggested_deadline: string
  }>
}

export interface AIFollowRecordParsed {
  follow_up_record: AIFollowUpRecord
}

// opportunitySuggestion 解析结果
export interface AIOpportunitySuggestionParsed {
  opportunity_suggestion: {
    /** 是否建议创建商机 */
    should_create: boolean
    /** 建议/不建议创建的原因 */
    create_reason: string
    opportunity_detail: {
      /** 商机名称 */
      opportunity_name: string
      /** 商机类型 */
      opportunity_type: { type_id: number; type_name: string }
      /** 客户预估类型 */
      customer_estimated_type: { type_id: number; type_name: string }
      /** 应用场景 */
      application_scenario: { scenario_id: number; scenario_name: string }
      /** 赢单率 0-100 */
      win_rate: string
      /** 进行中阶段 */
      in_progress_status: {stage_key: number; stage_name: string}
      /** 商机金额（元） */
      opportunity_amount: number
      /** EU/ISV */
      eu_isv: {type_id: number; type_name: string}
      /** 友商竞争情况 */
      competitor_competition: {type_ids: number; type_names: string}
      /** 友商可迁移产品 */
      competitor_migratable_product: string[] | null
      /** 商机联系人 */
      opportunity_contact: string
      /** 关注产品列表 */
      interested_products: string[]
      /** 预计订单时长（月） */
      estimated_order_duration_months: number
      /** 预计下单时间，ISO 8601 格式 */
      estimated_order_date: string
      /** 商机说明 */
      opportunity_description: string
    }
  }
}
